import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import type { ApprovalDetail, ApprovalListItem, AttendanceCategoryListItem, InvitationListItem } from '../appService';
import type { WorkspaceBootstrapContext } from './appTypes';
import { useAppRuntime, useWorkspaceBootstrapContext, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import {
  invalidateApprovalReviewQueries,
  invalidateAttendanceCategoryManagementQueries,
  invitationListQueryKey,
  useApprovalDetailQuery,
  useApprovalListQuery,
  useAttendanceCategoryListQuery,
  useContextMutationReset,
  useInvitationListQuery
} from './data';
import {
  ActionCard,
  ActionError,
  BackHeader,
  CompactEmptyRow,
  DiscardUnsavedChangesDialog,
  ModuleEmptyState,
  PendingButton,
  RecoverableReadError,
  SectionLoading,
  SvgIcon,
  TextField,
  formatActionError,
  getChoirAvatarLabel
} from './ui';

function AttendanceCategoryFormLeaveGuard({
  isDirty,
  isDisabled
}: {
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}): ReactNode {
  const blocker = useAttendanceCategoryFormLeaveBlocker({ isDirty, isDisabled });

  if (blocker.status !== 'blocked') {
    return null;
  }

  return (
    <DiscardUnsavedChangesDialog
      body="离开后，当前考勤类别更改不会保存。"
      onKeepEditing={blocker.reset}
      onDiscard={blocker.proceed}
    />
  );
}

export function ScheduleSettingsPage(): ReactNode {
  const navigate = useNavigate();
  const appContext = useWorkspaceBootstrapContext();
  const categories = useAttendanceCategoryListQuery();
  const canManageCategories = canManageAttendanceCategories(appContext);

  return (
    <div className="route-outlet">
      <BackHeader
        title="日程设置"
        action="back-to-choir-management"
        label="返回合唱团管理"
        onBack={() => {
          void navigate({ to: '/workspace/manage/choir', replace: true, resetScroll: true });
        }}
      />
      <section className="schedule-detail-section">
        <div className="section-heading-row">
          <h2>考勤类别</h2>
          {canManageCategories ? (
            <button
              type="button"
              className="text-button"
              data-action="create-attendance-category"
              onClick={() => {
                void navigate({
                  to: '/workspace/settings/attendance-categories/new',
                  resetScroll: true
                });
              }}
            >
              新增
            </button>
          ) : null}
        </div>
      </section>
      <AttendanceCategoryListContent categories={categories} canManage={canManageCategories} />
    </div>
  );
}

function AttendanceCategoryListContent({
  categories,
  canManage
}: {
  readonly categories: ReturnType<typeof useAttendanceCategoryListQuery>;
  readonly canManage: boolean;
}): ReactNode {
  if (categories.status === 'pending') {
    return <SectionLoading body="正在读取考勤类别。" />;
  }

  if (categories.status === 'error') {
    return (
      <RecoverableReadError
        error={categories.error}
        retryAction="retry-attendance-categories"
        onRetry={() => void categories.refetch()}
      />
    );
  }

  const sortedCategories = sortAttendanceCategories(categories.data);
  if (sortedCategories.length === 0) {
    return <ModuleEmptyState icon="calendar" body="暂无考勤类别" />;
  }

  return <AttendanceCategoryRows categories={sortedCategories} canManage={canManage} />;
}

function AttendanceCategoryRows({
  categories,
  canManage
}: {
  readonly categories: readonly AttendanceCategoryListItem[];
  readonly canManage: boolean;
}): ReactNode {
  const navigate = useNavigate();

  return (
    <section className="attendance-category-list prototype-list" role="list">
      {categories.map((category) => (
        <article
          className="attendance-category-row prototype-list-row"
          data-attendance-category-id={category.attendanceCategoryId}
          role="listitem"
          key={category.attendanceCategoryId}
        >
          <span className="attendance-category-copy member-copy">
            <strong>{category.name}</strong>
            <small className={`attendance-category-status ${category.status}`}>
              {formatAttendanceCategoryStatus(category.status)}
            </small>
          </span>
          {canManage ? (
            <span className="attendance-category-actions">
              <button
                type="button"
                className="text-button"
                data-action="rename-attendance-category"
                data-attendance-category-id={category.attendanceCategoryId}
                onClick={() => {
                  void navigate({
                    to: '/workspace/settings/attendance-categories/$attendanceCategoryId/rename',
                    params: { attendanceCategoryId: category.attendanceCategoryId },
                    resetScroll: true
                  });
                }}
              >
                重命名
              </button>
              {category.status === 'enabled' ? (
                <button
                  type="button"
                  className="text-button danger-text-button"
                  data-action="disable-attendance-category"
                  data-attendance-category-id={category.attendanceCategoryId}
                  onClick={() => {
                    void navigate({
                      to: '/workspace/settings/attendance-categories/$attendanceCategoryId/disable',
                      params: { attendanceCategoryId: category.attendanceCategoryId },
                      resetScroll: true
                    });
                  }}
                >
                  禁用
                </button>
              ) : null}
            </span>
          ) : null}
        </article>
      ))}
    </section>
  );
}

export function CreateAttendanceCategoryPage(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const createAttendanceCategory = useMutation({
    mutationFn: (name: string) => service.createAttendanceCategory({ workspaceContext, name }),
    onSuccess: async () => {
      await invalidateAttendanceCategoryManagementQueries(queryClient, workspaceContext, {
        affectsScheduleDisplayNames: false
      });
      void navigate({ to: '/workspace/settings/schedules', replace: true, resetScroll: true });
    }
  });

  if (!canManageAttendanceCategories(appContext)) {
    return <AttendanceCategoryPermissionDenied />;
  }

  return (
    <AttendanceCategoryFormRoute
      title="新建考勤类别"
      initialName=""
      savePending={createAttendanceCategory.isPending}
      isSubmitNavigationAllowed={isSubmitNavigationAllowed}
      onSubmit={async (name) => {
        setSubmitNavigationAllowed(true);
        try {
          await createAttendanceCategory.mutateAsync(name);
        } catch (error) {
          setSubmitNavigationAllowed(false);
          throw error;
        }
      }}
    />
  );
}

export function RenameAttendanceCategoryPage({
  attendanceCategoryId
}: {
  readonly attendanceCategoryId: string;
}): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const canManageCategories = canManageAttendanceCategories(appContext);
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const categories = useAttendanceCategoryListQuery(canManageCategories);
  const [isSubmitNavigationAllowed, setSubmitNavigationAllowed] = useState(false);
  const renameAttendanceCategory = useMutation({
    mutationFn: (name: string) => service.renameAttendanceCategory({ workspaceContext, attendanceCategoryId, name }),
    onSuccess: async () => {
      await invalidateAttendanceCategoryManagementQueries(queryClient, workspaceContext, {
        affectsScheduleDisplayNames: true
      });
      void navigate({ to: '/workspace/settings/schedules', replace: true, resetScroll: true });
    }
  });

  if (!canManageCategories) {
    return <AttendanceCategoryPermissionDenied />;
  }

  if (categories.status === 'pending') {
    return (
      <div className="route-outlet">
        <ScheduleSettingsBackHeader title="重命名考勤类别" pending={renameAttendanceCategory.isPending} />
        <SectionLoading body="正在读取考勤类别。" />
      </div>
    );
  }

  if (categories.status === 'error') {
    return (
      <div className="route-outlet">
        <ScheduleSettingsBackHeader title="重命名考勤类别" pending={renameAttendanceCategory.isPending} />
        <RecoverableReadError
          error={categories.error}
          retryAction="retry-rename-attendance-category"
          onRetry={() => void categories.refetch()}
        />
      </div>
    );
  }

  const category = findAttendanceCategory(categories.data, attendanceCategoryId);
  if (!category) {
    return (
      <div className="route-outlet">
        <ScheduleSettingsBackHeader title="重命名考勤类别" pending={renameAttendanceCategory.isPending} />
        <section className="panel error" role="alert" tabIndex={-1}>
          <p>找不到这个考勤类别。</p>
        </section>
      </div>
    );
  }

  return (
    <AttendanceCategoryFormRoute
      key={category.attendanceCategoryId}
      title="重命名考勤类别"
      initialName={category.name}
      savePending={renameAttendanceCategory.isPending}
      isSubmitNavigationAllowed={isSubmitNavigationAllowed}
      onSubmit={async (name) => {
        setSubmitNavigationAllowed(true);
        try {
          await renameAttendanceCategory.mutateAsync(name);
        } catch (error) {
          setSubmitNavigationAllowed(false);
          throw error;
        }
      }}
    />
  );
}

function AttendanceCategoryFormRoute({
  title,
  initialName,
  savePending,
  isSubmitNavigationAllowed,
  onSubmit
}: {
  readonly title: string;
  readonly initialName: string;
  readonly savePending: boolean;
  readonly isSubmitNavigationAllowed: boolean;
  readonly onSubmit: (name: string) => Promise<void>;
}): ReactNode {
  const navigate = useNavigate();
  const [name, setName] = useState(initialName);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const isDirty = name !== initialName;
  const leave = (): void => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    void navigate({ to: '/workspace/settings/schedules', replace: true, resetScroll: true });
  };
  const discardAndLeave = (): void => {
    setShowDiscardDialog(false);
    void navigate({ to: '/workspace/settings/schedules', replace: true, resetScroll: true });
  };

  return (
    <div className="route-outlet">
      <BackHeader
        title={title}
        action="back-to-schedule-settings"
        label="返回日程设置"
        pending={savePending}
        onBack={leave}
      />
      {showDiscardDialog ? (
        <DiscardUnsavedChangesDialog
          body="离开后，当前考勤类别更改不会保存。"
          onKeepEditing={() => setShowDiscardDialog(false)}
          onDiscard={discardAndLeave}
        />
      ) : null}
      <AttendanceCategoryFormLeaveGuard isDirty={isDirty} isDisabled={isSubmitNavigationAllowed} />
      <section className="attendance-category-form">
        <TextField
          label="类别名称"
          field="attendance-category-name"
          value={name}
          onChange={(nextName) => {
            setActionError(null);
            setName(nextName);
          }}
        />
      </section>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <PendingButton
          action="save-attendance-category"
          label="保存"
          variant="primary"
          pending={savePending}
          onClick={() => {
            void (async () => {
              setActionError(null);
              try {
                await onSubmit(name);
              } catch (error) {
                setActionError(formatActionError(error));
              }
            })();
          }}
        />
      </div>
    </div>
  );
}

export function DisableAttendanceCategoryPage({
  attendanceCategoryId
}: {
  readonly attendanceCategoryId: string;
}): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const canManageCategories = canManageAttendanceCategories(appContext);
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const categories = useAttendanceCategoryListQuery(canManageCategories);
  const [actionError, setActionError] = useState<string | null>(null);
  const disableAttendanceCategory = useMutation({
    mutationFn: () => service.disableAttendanceCategory({ workspaceContext, attendanceCategoryId }),
    onSuccess: async () => {
      await invalidateAttendanceCategoryManagementQueries(queryClient, workspaceContext, {
        affectsScheduleDisplayNames: false
      });
      void navigate({ to: '/workspace/settings/schedules', replace: true, resetScroll: true });
    }
  });

  if (!canManageCategories) {
    return <AttendanceCategoryPermissionDenied />;
  }

  if (categories.status === 'pending') {
    return (
      <div className="route-outlet">
        <ScheduleSettingsBackHeader title="确认禁用" pending={disableAttendanceCategory.isPending} />
        <SectionLoading body="正在读取考勤类别。" />
      </div>
    );
  }

  if (categories.status === 'error') {
    return (
      <div className="route-outlet">
        <ScheduleSettingsBackHeader title="确认禁用" pending={disableAttendanceCategory.isPending} />
        <RecoverableReadError
          error={categories.error}
          retryAction="retry-confirm-disable-attendance-category"
          onRetry={() => void categories.refetch()}
        />
      </div>
    );
  }

  const category = findAttendanceCategory(categories.data, attendanceCategoryId);

  return (
    <div className="route-outlet">
      <ScheduleSettingsBackHeader title="确认禁用" pending={disableAttendanceCategory.isPending} />
      {!category ? (
        <section className="panel error" role="alert" tabIndex={-1}>
          <p>找不到这个考勤类别。</p>
        </section>
      ) : (
        <section className="confirm-card">
          <h2>{`禁用“${category.name}”？`}</h2>
          <p>禁用后，这个类别不会再出现在新日程的考勤设置中。</p>
          <ActionError message={actionError} />
          <div className="footer-actions">
            <button
              type="button"
              className="secondary-button"
              data-action="cancel-disable-attendance-category"
              disabled={disableAttendanceCategory.isPending}
              onClick={() => {
                void navigate({
                  to: '/workspace/settings/schedules',
                  replace: true,
                  resetScroll: true
                });
              }}
            >
              取消
            </button>
            <PendingButton
              action="confirm-disable-attendance-category"
              label="确认禁用"
              variant="secondary"
              pending={disableAttendanceCategory.isPending}
              onClick={() => {
                void (async () => {
                  setActionError(null);
                  try {
                    await disableAttendanceCategory.mutateAsync();
                  } catch (error) {
                    setActionError(formatActionError(error));
                  }
                })();
              }}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function ScheduleSettingsBackHeader({
  title,
  pending = false
}: {
  readonly title: string;
  readonly pending?: boolean;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <BackHeader
      title={title}
      action="back-to-schedule-settings"
      label="返回日程设置"
      pending={pending}
      onBack={() => {
        void navigate({ to: '/workspace/settings/schedules', replace: true, resetScroll: true });
      }}
    />
  );
}

function AttendanceCategoryPermissionDenied(): ReactNode {
  return (
    <div className="route-outlet">
      <ScheduleSettingsBackHeader title="日程设置" />
      <section className="panel" role="alert" tabIndex={-1}>
        <p>无权管理考勤类别。</p>
      </section>
    </div>
  );
}

export function ChoirManagementPage(): ReactNode {
  const { service } = useAppRuntime();
  const appContext = useWorkspaceBootstrapContext();
  const resetAfterContextMutation = useContextMutationReset();
  const navigate = useNavigate();
  const canReviewApprovals = canApproveJoinRequests(appContext);
  const leaveCurrentChoirWorkspace = useMutation({
    mutationFn: () => service.leaveCurrentChoirWorkspace()
  });
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <div className="route-outlet">
      <BackHeader
        title="合唱团管理"
        action="back-to-workspace-home"
        label="返回首页"
        onBack={() => {
          void navigate({
            to: '/workspace/$tab',
            params: { tab: 'home' },
            replace: true,
            resetScroll: true
          });
        }}
      />
      <ManagementContextSummary choirName={appContext.currentChoir.name} seasonName={appContext.currentSeason.name} />
      <section className="management-action-list prototype-list">
        <ActionCard
          action="open-member-settings"
          label="成员设置"
          description="成员组与当前乐季成员"
          icon="users"
          variant="menu"
          onClick={() => {
            void navigate({ to: '/workspace/settings/members', resetScroll: true });
          }}
        />
        <ActionCard
          action="open-schedule-settings"
          label="日程设置"
          description="日程和考勤类别"
          icon="calendar"
          variant="menu"
          onClick={() => {
            void navigate({ to: '/workspace/settings/schedules', resetScroll: true });
          }}
        />
        <ActionCard
          action="open-season-settings"
          label="乐季设置"
          description="当前乐季基础信息"
          icon="calendar"
          variant="menu"
          onClick={() => {
            void navigate({ to: '/workspace/settings/season', resetScroll: true });
          }}
        />
        <ActionCard
          action="open-invitation-codes"
          label="邀请码"
          description="生成或删除当前可用的邀请码"
          icon="mail"
          variant="menu"
          onClick={() => {
            void navigate({ to: '/workspace/manage/invitations', resetScroll: true });
          }}
        />
        {canReviewApprovals ? (
          <ActionCard
            action="open-approval-requests"
            label="审批申请"
            description="处理成员加入申请"
            icon="users"
            variant="menu"
            onClick={() => {
              void navigate({ to: '/workspace/manage/approvals', resetScroll: true });
            }}
          />
        ) : null}
      </section>
      <ActionError message={actionError} />
      <PendingButton
        action="leave-choir-workspace"
        label="退出当前合唱团工作区"
        variant="secondary"
        pending={leaveCurrentChoirWorkspace.isPending}
        onClick={() => {
          void (async () => {
            setActionError(null);
            try {
              await leaveCurrentChoirWorkspace.mutateAsync();
              await resetAfterContextMutation();
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

function ManagementContextSummary({
  choirName,
  seasonName
}: {
  readonly choirName: string;
  readonly seasonName: string;
}): ReactNode {
  return (
    <section className="management-context-summary" aria-label="当前管理空间">
      <span className="choir-avatar" aria-hidden="true">
        {getChoirAvatarLabel(choirName)}
      </span>
      <span className="management-context-copy">
        <strong>{choirName}</strong>
        <small>{seasonName}</small>
      </span>
    </section>
  );
}

export function ApprovalListPage(): ReactNode {
  const navigate = useNavigate();
  const appContext = useWorkspaceBootstrapContext();
  const canReviewApprovals = canApproveJoinRequests(appContext);
  const approvals = useApprovalListQuery(canReviewApprovals);

  return (
    <div className="route-outlet">
      <BackHeader
        title="加入审批"
        action="back-to-choir-management"
        label="返回合唱团管理"
        onBack={() => {
          void navigate({ to: '/workspace/manage/choir', replace: true, resetScroll: true });
        }}
      />
      <ApprovalListContent approvals={approvals} canReviewApprovals={canReviewApprovals} />
    </div>
  );
}

function ApprovalListContent({
  approvals,
  canReviewApprovals
}: {
  readonly approvals: ReturnType<typeof useApprovalListQuery>;
  readonly canReviewApprovals: boolean;
}): ReactNode {
  if (!canReviewApprovals) {
    return <ApprovalPermissionDenied />;
  }

  if (approvals.status === 'pending') {
    return <SectionLoading body="正在读取加入申请。" />;
  }

  if (approvals.status === 'error') {
    if (isApprovalPermissionError(approvals.error)) {
      return <ApprovalPermissionDenied />;
    }

    return (
      <RecoverableReadError
        error={approvals.error}
        retryAction="retry-approval-list"
        onRetry={() => void approvals.refetch()}
      />
    );
  }

  if (approvals.data.length === 0) {
    return <CompactEmptyRow body="暂无待审批申请" />;
  }

  return <ApprovalRows approvals={approvals.data} />;
}

function ApprovalRows({ approvals }: { readonly approvals: readonly ApprovalListItem[] }): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();

  return (
    <section className="approval-list prototype-list" role="list">
      {approvals.map((approval) => (
        <button
          type="button"
          className="approval-row prototype-list-row"
          data-action="open-approval-request"
          data-join-request-id={approval.joinRequestId}
          aria-label={`打开${approval.nickname}的加入申请`}
          key={approval.joinRequestId}
          onClick={() => {
            routeAdapter.openApprovalDetail(approval.joinRequestId);
          }}
        >
          <span className="member-avatar member-avatar-icon" aria-hidden="true">
            <SvgIcon name="users" className="member-avatar-svg" />
          </span>
          <span className="member-copy">
            <strong>{approval.nickname}</strong>
            <small>{formatApprovalRowMeta(approval)}</small>
          </span>
          <span className="choir-context-chevron" aria-hidden="true">
            ›
          </span>
        </button>
      ))}
    </section>
  );
}

export function ApprovalDetailPage({ joinRequestId }: { readonly joinRequestId: string }): ReactNode {
  const { service } = useAppRuntime();
  const appContext = useWorkspaceBootstrapContext();
  const workspaceContext = useWorkspaceContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canReviewApprovals = canApproveJoinRequests(appContext);
  const approval = useApprovalDetailQuery(joinRequestId, canReviewApprovals);
  const [selectedMemberGroupIds, setSelectedMemberGroupIds] = useState<readonly string[]>([]);
  const [decisionMode, setDecisionMode] = useState<'approve' | 'reject'>('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const approve = useMutation({
    mutationFn: () =>
      service.approveJoinRequest({
        workspaceContext,
        joinRequestId,
        memberGroupIds: selectedMemberGroupIds
      }),
    onSuccess: async () => {
      await navigate({
        to: '/workspace/manage/approvals',
        replace: true,
        resetScroll: true
      });
      await invalidateApprovalReviewQueries(queryClient, workspaceContext, joinRequestId);
    }
  });
  const reject = useMutation({
    mutationFn: () =>
      service.rejectJoinRequest({
        workspaceContext,
        joinRequestId,
        rejectionReason
      }),
    onSuccess: async () => {
      await navigate({
        to: '/workspace/manage/approvals',
        replace: true,
        resetScroll: true
      });
      await invalidateApprovalReviewQueries(queryClient, workspaceContext, joinRequestId);
    }
  });
  const actionPending = approve.isPending || reject.isPending;

  return (
    <div className="route-outlet">
      <BackHeader
        title="审批详情"
        action="back-to-approval-list"
        label="返回加入审批"
        pending={actionPending}
        onBack={() => {
          void navigate({ to: '/workspace/manage/approvals', replace: true, resetScroll: true });
        }}
      />
      <ApprovalDetailContent
        approval={approval}
        canReviewApprovals={canReviewApprovals}
        selectedMemberGroupIds={selectedMemberGroupIds}
        decisionMode={decisionMode}
        rejectionReason={rejectionReason}
        actionPending={actionPending}
        actionError={actionError}
        onToggleMemberGroup={(memberGroupId) => {
          setSelectedMemberGroupIds((current) =>
            current.includes(memberGroupId)
              ? current.filter((candidate) => candidate !== memberGroupId)
              : [...current, memberGroupId]
          );
        }}
        onStartReject={() => {
          setActionError(null);
          setDecisionMode('reject');
        }}
        onCancelReject={() => {
          setActionError(null);
          setDecisionMode('approve');
          setRejectionReason('');
        }}
        onRejectionReasonChange={setRejectionReason}
        onApprove={() => {
          void (async () => {
            setActionError(null);
            try {
              await approve.mutateAsync();
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
        onReject={() => {
          void (async () => {
            setActionError(null);
            try {
              await reject.mutateAsync();
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

function ApprovalDetailContent({
  approval,
  canReviewApprovals,
  selectedMemberGroupIds,
  decisionMode,
  rejectionReason,
  actionPending,
  actionError,
  onToggleMemberGroup,
  onStartReject,
  onCancelReject,
  onRejectionReasonChange,
  onApprove,
  onReject
}: {
  readonly approval: ReturnType<typeof useApprovalDetailQuery>;
  readonly canReviewApprovals: boolean;
  readonly selectedMemberGroupIds: readonly string[];
  readonly decisionMode: 'approve' | 'reject';
  readonly rejectionReason: string;
  readonly actionPending: boolean;
  readonly actionError: string | null;
  readonly onToggleMemberGroup: (memberGroupId: string) => void;
  readonly onStartReject: () => void;
  readonly onCancelReject: () => void;
  readonly onRejectionReasonChange: (value: string) => void;
  readonly onApprove: () => void;
  readonly onReject: () => void;
}): ReactNode {
  if (!canReviewApprovals) {
    return <ApprovalPermissionDenied />;
  }

  if (approval.status === 'pending') {
    return <SectionLoading body="正在读取加入申请。" />;
  }

  if (approval.status === 'error') {
    if (isApprovalPermissionError(approval.error)) {
      return <ApprovalPermissionDenied />;
    }

    if (isMissingApprovalRequestError(approval.error)) {
      return <MissingApprovalRequest />;
    }

    return (
      <RecoverableReadError
        error={approval.error}
        retryAction="retry-approval-detail"
        onRetry={() => void approval.refetch()}
      />
    );
  }

  return (
    <>
      <ApprovalApplicantSummary approval={approval.data} />
      <ActionError message={actionError} />
      {decisionMode === 'approve' ? (
        <ApprovalDecisionPanel
          approval={approval.data}
          selectedMemberGroupIds={selectedMemberGroupIds}
          actionPending={actionPending}
          onToggleMemberGroup={onToggleMemberGroup}
          onApprove={onApprove}
          onStartReject={onStartReject}
        />
      ) : (
        <RejectionDecisionPanel
          rejectionReason={rejectionReason}
          actionPending={actionPending}
          onRejectionReasonChange={onRejectionReasonChange}
          onReject={onReject}
          onCancelReject={onCancelReject}
        />
      )}
    </>
  );
}

function ApprovalPermissionDenied(): ReactNode {
  return (
    <section className="panel" role="alert" tabIndex={-1}>
      <p>当前账号没有审批加入申请的权限</p>
    </section>
  );
}

function MissingApprovalRequest(): ReactNode {
  return <CompactEmptyRow body="该申请已处理，或不在当前待审批列表中。" />;
}

function ApprovalApplicantSummary({ approval }: { readonly approval: ApprovalDetail }): ReactNode {
  return (
    <section className="schedule-detail-section">
      <div className="confirmed-field">
        <span>昵称</span>
        <strong>{approval.nickname}</strong>
      </div>
      <div className="confirmed-field">
        <span>邮箱</span>
        <strong>{approval.applicantEmail}</strong>
      </div>
      <div className="confirmed-field">
        <span>申请时间</span>
        <strong>{formatShortDateTime(approval.submittedAt)}</strong>
      </div>
    </section>
  );
}

function ApprovalDecisionPanel({
  approval,
  selectedMemberGroupIds,
  actionPending,
  onToggleMemberGroup,
  onApprove,
  onStartReject
}: {
  readonly approval: ApprovalDetail;
  readonly selectedMemberGroupIds: readonly string[];
  readonly actionPending: boolean;
  readonly onToggleMemberGroup: (memberGroupId: string) => void;
  readonly onApprove: () => void;
  readonly onStartReject: () => void;
}): ReactNode {
  return (
    <section className="schedule-detail-section">
      <div className="section-heading-row">
        <h2>成员组</h2>
      </div>
      <div className="approval-member-group-choices">
        {approval.memberGroupChoices.map((memberGroup) => (
          <label className="approval-member-group-choice" key={memberGroup.memberGroupId}>
            <input
              type="checkbox"
              data-member-group-id={memberGroup.memberGroupId}
              checked={selectedMemberGroupIds.includes(memberGroup.memberGroupId)}
              disabled={actionPending}
              onChange={() => onToggleMemberGroup(memberGroup.memberGroupId)}
            />
            <span>{memberGroup.name}</span>
          </label>
        ))}
      </div>
      <div className="request-actions">
        <PendingButton
          action="approve-join-request"
          label="通过申请"
          variant="primary"
          pending={actionPending}
          onClick={onApprove}
        />
        <PendingButton
          action="start-reject-join-request"
          label="拒绝申请"
          variant="secondary"
          pending={actionPending}
          onClick={onStartReject}
        />
      </div>
    </section>
  );
}

function RejectionDecisionPanel({
  rejectionReason,
  actionPending,
  onRejectionReasonChange,
  onReject,
  onCancelReject
}: {
  readonly rejectionReason: string;
  readonly actionPending: boolean;
  readonly onRejectionReasonChange: (value: string) => void;
  readonly onReject: () => void;
  readonly onCancelReject: () => void;
}): ReactNode {
  return (
    <section className="schedule-detail-section">
      <label className="field" data-field="rejection-reason">
        <span>拒绝理由</span>
        <textarea
          name="rejectionReason"
          value={rejectionReason}
          disabled={actionPending}
          onChange={(event) => onRejectionReasonChange(event.currentTarget.value)}
        />
      </label>
      <div className="request-actions">
        <PendingButton
          action="cancel-reject-join-request"
          label="取消"
          variant="secondary"
          pending={actionPending}
          onClick={onCancelReject}
        />
        <PendingButton
          action="reject-join-request"
          label="确认拒绝"
          variant="primary"
          pending={actionPending}
          onClick={onReject}
        />
      </div>
    </section>
  );
}

export function InvitationCodesPage(): ReactNode {
  const { service } = useAppRuntime();
  const appContext = useWorkspaceBootstrapContext();
  const workspaceContext = useWorkspaceContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invitations = useInvitationListQuery();
  const canDeleteInvitations = canDeleteInvitationCodes(appContext);
  const [actionError, setActionError] = useState<string | null>(null);
  const generateInvitation = useMutation({
    mutationFn: () => service.generateInvitationCode({ workspaceContext }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: invitationListQueryKey(workspaceContext),
        exact: true
      });
    }
  });
  const deleteInvitation = useMutation({
    mutationFn: (invitationCodeId: string) => service.deleteInvitationCode({ workspaceContext, invitationCodeId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: invitationListQueryKey(workspaceContext),
        exact: true
      });
    }
  });

  return (
    <div className="route-outlet">
      <BackHeader
        title="邀请码"
        action="back-to-choir-management"
        label="返回合唱团管理"
        onBack={() => {
          void navigate({ to: '/workspace/manage/choir', replace: true, resetScroll: true });
        }}
      />
      <section className="schedule-detail-section">
        <div className="section-heading-row">
          <h2>当前可用</h2>
          <PendingButton
            action="generate-invitation"
            label="生成新码"
            variant="secondary"
            pending={generateInvitation.isPending}
            onClick={() => {
              void (async () => {
                setActionError(null);
                try {
                  await generateInvitation.mutateAsync();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </section>
      <ActionError message={actionError} />
      <InvitationListContent
        invitations={invitations}
        canDelete={canDeleteInvitations}
        deletingInvitationCodeId={deleteInvitation.isPending ? (deleteInvitation.variables ?? null) : null}
        onDelete={(invitationCodeId) => {
          void (async () => {
            setActionError(null);
            try {
              await deleteInvitation.mutateAsync(invitationCodeId);
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

function InvitationListContent({
  invitations,
  canDelete,
  deletingInvitationCodeId,
  onDelete
}: {
  readonly invitations: ReturnType<typeof useInvitationListQuery>;
  readonly canDelete: boolean;
  readonly deletingInvitationCodeId: string | null;
  readonly onDelete: (invitationCodeId: string) => void;
}): ReactNode {
  if (invitations.status === 'pending') {
    return <SectionLoading body="正在读取邀请码。" />;
  }

  if (invitations.status === 'error') {
    return (
      <RecoverableReadError
        error={invitations.error}
        retryAction="retry-invitation-list"
        onRetry={() => void invitations.refetch()}
      />
    );
  }

  if (invitations.data.length === 0) {
    return <CompactEmptyRow body="暂无可用邀请码" />;
  }

  return (
    <section className="invitation-list prototype-list" role="list">
      {invitations.data.map((invitation) => (
        <article className="invitation-row prototype-list-row" role="listitem" key={invitation.invitationCodeId}>
          <span className="member-copy">
            <strong>{invitation.code}</strong>
            <small>{formatInvitationRowMeta(invitation)}</small>
          </span>
          {canDelete ? (
            <button
              type="button"
              className="delete-icon"
              data-action="delete-invitation"
              data-invitation-code-id={invitation.invitationCodeId}
              aria-label={`删除邀请码${invitation.code}`}
              aria-busy={deletingInvitationCodeId === invitation.invitationCodeId ? 'true' : undefined}
              disabled={deletingInvitationCodeId === invitation.invitationCodeId}
              onClick={() => onDelete(invitation.invitationCodeId)}
            >
              <SvgIcon name="trash" className="delete-icon-svg" />
            </button>
          ) : null}
        </article>
      ))}
    </section>
  );
}

export function RoutePlaceholder({ title }: { readonly title: string }): ReactNode {
  return (
    <div className="route-outlet">
      <h1 className="page-title" tabIndex={-1}>
        {title}
      </h1>
    </div>
  );
}

function useAttendanceCategoryFormLeaveBlocker({
  isDirty,
  isDisabled
}: {
  readonly isDirty: boolean;
  readonly isDisabled: boolean;
}) {
  return useBlocker({
    withResolver: true,
    enableBeforeUnload: () => isDirty && !isDisabled,
    disabled: !isDirty || isDisabled,
    shouldBlockFn: ({ current, next }) => {
      if (!isAttendanceCategoryFormPath(current.pathname)) {
        return false;
      }

      return !isAttendanceCategoryFormPath(next.pathname);
    }
  });
}

function isAttendanceCategoryFormPath(pathname: string): boolean {
  return /^\/workspace\/settings\/attendance-categories\/(?:new|[^/]+\/rename)$/.test(pathname);
}

function formatApprovalRowMeta(approval: ApprovalListItem): string {
  return `${approval.applicantEmail} · ${formatShortDateTime(approval.submittedAt)}`;
}

function formatInvitationRowMeta(invitation: InvitationListItem): string {
  return [
    formatInvitationStatus(invitation.status),
    `生成 ${formatShortDateTime(invitation.createdAt)}`,
    `到期 ${formatShortDateTime(invitation.expiresAt)}`
  ].join(' · ');
}

function formatInvitationStatus(status: InvitationListItem['status']): string {
  return status === 'active' ? '可用' : '已删除';
}

function formatShortDateTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid timestamp: ${isoTimestamp}`);
  }
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

function canManageAttendanceCategories(appContext: WorkspaceBootstrapContext): boolean {
  return appContext.isCurrentUserAdmin && appContext.currentMembership.role === 'admin';
}

function canDeleteInvitationCodes(appContext: WorkspaceBootstrapContext): boolean {
  return appContext.isCurrentUserAdmin && appContext.currentMembership.role === 'admin';
}

function canApproveJoinRequests(appContext: WorkspaceBootstrapContext): boolean {
  return appContext.currentPermissionKeys.includes('member_approval');
}

function isApprovalPermissionError(error: Error): boolean {
  return (
    error.message === 'Choir admin permission is required' ||
    error.message === 'forbidden: Forbidden' ||
    error.message.startsWith('forbidden:')
  );
}

function isMissingApprovalRequestError(error: Error): boolean {
  return (
    error.message === 'Join request not found' ||
    error.message === 'join_request_not_found: Join request not found' ||
    error.message.startsWith('join_request_not_found:')
  );
}

function sortAttendanceCategories(
  categories: readonly AttendanceCategoryListItem[]
): readonly AttendanceCategoryListItem[] {
  return [...categories].sort(compareAttendanceCategoriesByDisplayOrder);
}

function compareAttendanceCategoriesByDisplayOrder(
  left: AttendanceCategoryListItem,
  right: AttendanceCategoryListItem
): number {
  if (left.displayOrder !== right.displayOrder) {
    return left.displayOrder - right.displayOrder;
  }
  return left.name.localeCompare(right.name);
}

function formatAttendanceCategoryStatus(status: AttendanceCategoryListItem['status']): string {
  return status === 'enabled' ? '启用中' : '已禁用';
}

function findAttendanceCategory(
  categories: readonly AttendanceCategoryListItem[],
  attendanceCategoryId: string
): AttendanceCategoryListItem | null {
  return categories.find((category) => category.attendanceCategoryId === attendanceCategoryId) ?? null;
}
